import { cache, mutate } from 'swr';
import { NekoError } from './errors';

// This function allows to invalidate a SWR key which might
// have been use for paging, for example: swrInvalidateMany('/api/items')
// will reset everything which happens around this key.

function swrInvalidateMany(select) {
  const keys = cache.keys();
  const mutations = [];
  let regexp = new RegExp(`(.*)${select}(.*)`);
  for (let key of keys) {
    if (regexp.test(key)) {
      mutations.push(mutate(key, null, true));
    }
  }
  return Promise.all(mutations);
}

function chunkArrayInGroups(arr, size) {
  let result = [];
  for (let i = 0; i < arr.length; i += size)
    result.push(arr.slice(i, i + size));
  return result;
}

const consolidateJsonResult = (json) => {
  if (!json.data) {
    return json;
  }
  if (json.data.length > 0 && json.data[0].meta) {
    for (let x of json.data) {
      try { x.meta = JSON.parse(x.meta); }
      catch (err) { console.error('[JsonFetcher]', 'Could not decode meta.', x.meta); }
    }
  }
  else if (json.data.meta) {
    try { json.data.meta = JSON.parse(json.data.meta); }
    catch (err) { console.error('[JsonFetcher]', 'Could not decode meta.', x.meta); }
  }
  return json;
}

const jsonFetcher = async (url, options = {}) => {
  let body = null;
  let json = {};
  let nekoError = null;
  let rawBody = null;

  try {
    options = options ? options : {};
    options.headers = options.headers ? options.headers : {};
    options.headers['Pragma'] = 'no-cache';
    options.headers['Cache-Control'] = 'no-cache';
    rawBody = await fetch(`${url}`, options);
    body = await rawBody.text();
    json = JSON.parse(body);
    if (!json.success) {
      let code = json.success === false ? 'NOT-SUCCESS' : 'N/A';
      let message = json.message ? json.message : 'Unknown error. Check your Console Logs.';
      if (json.code === 'rest_no_route') {
        message = "The API can't be accessed. Is Rest API enabled?";
        code = 'NO-ROUTE';
      }
      else if (json.code === 'internal_server_error') {
        message = "Server error. Please check your PHP Error Logs.";
        code = 'SERVER-ERROR';
      }
      nekoError = new NekoError(message, code, url, body ? body : rawBody);
    }
  }
  catch (error) {
    let code = 'BROKEN-REPLY';
    let message = "The reply sent by the server is broken.";
    if (rawBody && rawBody.status) {
      if (rawBody.status === 408) {
        code = "REQUEST-TIMEOUT";
        message = "The request generated a timeout.";
      }
    }
    nekoError = new NekoError(message, code, url, body ? body : rawBody, error);
  }

  // Rewrite the Json if there is an error
  if (nekoError) {
    console.error('[NekoError] JsonFetcher', nekoError.url, { code: nekoError.code, 
      error: nekoError.error, body: nekoError.body });
    json.success = false;
    json.error = nekoError;
  }
  return consolidateJsonResult(json);
}

const getFetch = async (url, json, signal, nonce) => {
  const nonceHeader = nonce ? { 'X-WP-Nonce': nonce } : {};
  return jsonFetcher(url, { 
    method: 'GET', 
    headers: { 'Content-Type': 'application/json', ...nonceHeader },
    signal: signal
  });
}

const postFetch = async (url, config = {}) => {
  const { json = {}, signal, file, nonce } = config;
  let formData = file ? new FormData() : null;
  if (file) {
    formData.append('file', file);
    for (const [key, value] of Object.entries(json)) {
      formData.append(key, value);
    }
  }

  const headers = nonce ? { 'X-WP-Nonce': nonce } : {};
  if (!formData) {
    headers['Content-Type'] = 'application/json';
  }

  return jsonFetcher(url, { 
    method: 'POST',
    headers: headers,
    body: formData ? formData :JSON.stringify(json) ,
    signal: signal
  });
}

const buildUrlWithParams = (apiUrl, params) => {
  const isPlainPermalink = apiUrl.includes('index.php?rest_route');
  const urlParams = new URLSearchParams(params);
  const finalUrl = apiUrl + (isPlainPermalink ? '&' : '?') + urlParams.toString();
  return finalUrl;
}

const formatBytes = (bytes, decimals = 2) => {
  //if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  let i = bytes > 0 ? Math.floor(Math.log(bytes) / Math.log(k)) : 0;
  if ( sizes[i] === 'Bytes' ) {
    bytes *= 100;
    i = 1;
  }
  bytes = parseFloat((bytes / Math.pow(k, i)).toFixed(dm));
  var str = bytes.toFixed(Math.max(dm, (bytes.toString().split('.')[1] || []).length));
  return str + ' ' + sizes[i];
  
}

export { getFetch, postFetch, jsonFetcher, swrInvalidateMany, chunkArrayInGroups, buildUrlWithParams, formatBytes };